import { gql, TypedDocumentNode } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useTranslation } from 'next-i18next/pages';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';

import { useJobPositionIdContext } from '@pflegia/ui/contexts/hooks/use-job-position-id-context';
import Spinner from '@pflegia/ui/shared/spinner';
import AnonymousWrapper from '@pflegia/ui/shared/wrapper/AnonymousWrapper';
import { isMobile } from '@pflegia/util/functions/device-detection';

import { FUNNEL } from '@shared/application/constants';

import { truncateEmail } from '@utils/truncate-email';
import { truncatePhoneNumber } from '@utils/truncate-phone';

export type PublicContactType = 'email' | 'phone';

interface CemPlusDetailsProps {
  contactDetail: string;
  loading?: boolean;
  textOnly?: boolean;
  truncate?: (text: string) => string;
  type: PublicContactType;
}

interface CemPlusContactInfoProps {
  anonymous: boolean;
  loading?: boolean;
  publicPhoneNumber?: string | null;
  publicEmail?: string | null;
  formalLanguage?: boolean | null;
}

export const CemPlusDetails = ({ contactDetail, textOnly, type, truncate = (str: string) => str, loading }: CemPlusDetailsProps) => {
  const shouldRevealByDefault = truncate(contactDetail) === contactDetail;
  const [showPhone, setShowPhone] = useState(shouldRevealByDefault);
  const router = useRouter();
  const [track] = useMutation(TRACK_JOB_POSITION_PUBLIC_CONTACT_CLICK);
  const id = useJobPositionIdContext();
  const isClickTracked = useRef<boolean>(false);

  useEffect(() => {
    setShowPhone(shouldRevealByDefault || Boolean(isMobile()));
  }, [shouldRevealByDefault]);

  if (loading) return <Spinner />;

  const emojiRegex = /(☎️|📧): /gu;
  const contactEmoji = contactDetail.match(emojiRegex);
  const contactValue = contactDetail.replace(emojiRegex, '');
  const contactAction = `${type === 'email' ? 'mailto' : 'tel'}:${contactValue}`;

  const handlepublicPhoneNumberClick = () => {
    if (!isClickTracked.current) {
      track({
        variables: {
          jobPositionId: id,
          contactType: type,
        },
      });
      isClickTracked.current = true;
    }

    if (!showPhone) {
      setShowPhone(true);
      return;
    }
    router.push(contactAction);
  };

  return textOnly ? (
    <span onClick={handlepublicPhoneNumberClick} className='cursor-pointer text-inherit'>
      {contactEmoji}
      <span className='text-main-blue truncate font-medium'>{showPhone ? contactValue : truncate(contactValue)}</span>
    </span>
  ) : (
    <button className='truncate text-start' onClick={handlepublicPhoneNumberClick} type='button'>
      {type === 'email' ? '📧' : '☎️'} <span className='ml-2 hover:underline'>{showPhone ? contactDetail : truncate(contactDetail)}</span>
    </button>
  );
};

const CemPlusContactInfo = ({ publicPhoneNumber, publicEmail, anonymous, loading, formalLanguage = false }: CemPlusContactInfoProps) => {
  const { t } = useTranslation('jobDetails', { keyPrefix: 'contactPerson' });
  return (
    <div className='flex w-full flex-col gap-2'>
      <h2 className='text-default mb-3 font-medium'>{formalLanguage ? t('cemPlus.formalTitle') : t('cemPlus.informalTitle')}</h2>
      <AnonymousWrapper anonymous={anonymous} href={FUNNEL}>
        <div className='text-main-blue flex w-full flex-col gap-2 font-medium'>
          {publicPhoneNumber && (
            <CemPlusDetails loading={loading} type='phone' truncate={truncatePhoneNumber} contactDetail={publicPhoneNumber} />
          )}
          {publicEmail && <CemPlusDetails loading={loading} type='email' truncate={truncateEmail} contactDetail={publicEmail} />}
        </div>
      </AnonymousWrapper>
    </div>
  );
};

export default CemPlusContactInfo;

export interface TrackJobPositionPublicContactClickMutation {
  result: boolean;
}

export interface TrackJobPositionPublicContactClickMutationVariables {
  jobPositionId?: string;
  contactType: PublicContactType;
}

export const TRACK_JOB_POSITION_PUBLIC_CONTACT_CLICK: TypedDocumentNode<
  TrackJobPositionPublicContactClickMutation,
  TrackJobPositionPublicContactClickMutationVariables
> = gql`
  mutation TrackJobPositionPublicContactClick($jobPositionId: String!, $contactType: String!) {
    result: trackJobPositionPublicContactClick(jobPositionId: $jobPositionId, contactType: $contactType)
  }
`;
